import * as React from 'react';
import { ScrollFlex, useStyles } from '../styled';
import { AbsoluteBox, FlexBox, SvgIconButton, useTheme, useMediaQuery, SvgIcon } from '@kis-technologies/kis-ui';
import { ScrollbarProps } from '../types';

const getOffsetValue = (
  el: React.RefObject<HTMLDivElement> | null,
  offset: 'offsetHeight' | 'scrollTop' | 'scrollHeight' | 'clientHeight'
) => {
  return (el && el.current && el.current[offset]) || 0;
};

/**
 * Scrollbar is customized scrollbar with up and down arrows to navigate through a list
 */

export const Scrollbar = (props: ScrollbarProps) => {
  const { children, elToFocus, onScrollDownClick, onScrollUpClick, hideScrollBar, refreshModel } = props;

  const containerEl = React.useRef<HTMLDivElement>(null);
  const scrollEl = React.useRef<HTMLDivElement>(null);

  const [hasScroll, setHasScroll] = React.useState(false);
  const [minHeight, setMinHeight] = React.useState(false);
  const [maxHeight, setMaxHeight] = React.useState(false);

  let scrollElHeight = getOffsetValue(scrollEl, 'offsetHeight');
  let containerElHeight = getOffsetValue(containerEl, 'offsetHeight');

  const theme = useTheme();
  if (useMediaQuery(theme.breakpoints.down('lg'))) {
    scrollElHeight = getOffsetValue(scrollEl, 'offsetHeight');
    containerElHeight = getOffsetValue(containerEl, 'offsetHeight');
  }
  const classes = useStyles({ hasScroll })();

  // On Mount
  React.useEffect(() => {
    // Add scroll event listener
    document.addEventListener('scroll', handleScroll, true);

    // Call handleScroll
    handleScroll();

    // Call focusEl
    focusEl();

    // On Unmount - Remove scroll event listener
    return () => document.removeEventListener('scroll', handleScroll, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On Update of scrollEl || containerEl
  React.useEffect(() => {
    const scrollHeight = getOffsetValue(scrollEl, 'offsetHeight');
    const containerHeight = getOffsetValue(containerEl, 'offsetHeight');

    if (hideScrollBar) {
      setHasScroll(false);
    } else {
      // Compare scrollEl height and containerEl height - set scroll to true if scrollEl height is greater
      if (scrollHeight > containerHeight) {
        setHasScroll(true);
      } else {
        setHasScroll(false);
      }
    }
  }, [scrollElHeight, containerElHeight, hideScrollBar, refreshModel]);

  // ***************
  // ** FUNCTIONS **
  // ***************

  // focus element
  const focusEl = () => {
    const el = elToFocus && elToFocus.current;

    // focus the elToFocus if it exists
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  // handle scroll
  const handleScroll = () => {
    const scrollTop = getOffsetValue(containerEl, 'scrollTop');
    const scrollHeight = getOffsetValue(containerEl, 'scrollHeight');
    const clientHeight = getOffsetValue(containerEl, 'clientHeight');

    // Determines if the scrollable items are at max height, min height, or neither
    if (scrollHeight - scrollTop === clientHeight) {
      setMaxHeight(true);
      setMinHeight(false);
    } else if (!scrollTop) {
      setMinHeight(true);
      setMaxHeight(false);
    } else {
      setMinHeight(false);
      setMaxHeight(false);
    }
  };

  // up arrow click
  const handleScrollUpClick = () => {
    const container = containerEl && containerEl.current;
    (container as HTMLDivElement).scrollBy(0, -100);

    // scrolls the items by 100 pixels
    if (onScrollUpClick) onScrollUpClick(container);
  };

  // down arrow click
  const handleScrollDownClick = () => {
    const container = containerEl && containerEl.current;
    (container as HTMLDivElement).scrollBy(0, 100);

    // scrolls the items by 100 pixels
    if (onScrollDownClick) onScrollDownClick(container);
  };

  return (
    <FlexBox direction="column" position="relative" noWrap maxHeight="100%" overflow="hidden">
      {/* Scroll Container with items */}
      {hideScrollBar ? (
        <FlexBox>{children}</FlexBox>
      ) : (
        <ScrollFlex ref={containerEl} hasScroll={hasScroll} className={classes.scrollbarFlex}>
          {React.Children.map(children, (child) => {
            if (!React.isValidElement<{ layoutRef: any }>(child)) return null;

            return React.cloneElement(child, {
              layoutRef: scrollEl
            });
          })}
        </ScrollFlex>
      )}

      {/* Only display up and down arrows if items are scrollable */}
      {hasScroll && (
        <>
          {/* Top Fade */}
          {!minHeight && (
            <AbsoluteBox
              top="0"
              right="0"
              width="100%"
              height={50}
              sx={{
                background: `linear-gradient(to bottom, rgb(255,255,255) 0%, rgba(255,255,255,0.67) 33%, rgba(255,255,255,0.33) 67%, rgba(255,255,255,0.1) 100%)`,
                pointerEvents: 'none'
              }}
            />
          )}
          {/* Up Arrow Button */}
          <AbsoluteBox top="0px" right="-2px" height={52}>
            <SvgIconButton disabled={minHeight} onClick={handleScrollUpClick} data-testid="scroll-up-button">
              <SvgIcon name="chevron_up_circle" size={52} />
            </SvgIconButton>
          </AbsoluteBox>
          {/* Bottom Fade */}
          {!maxHeight && (
            <AbsoluteBox
              bottom="0"
              right="0"
              width="100%"
              height={50}
              sx={{
                background: `linear-gradient(to top, rgb(255,255,255) 0%, rgba(255,255,255,0.67) 33%, rgba(255,255,255,0.33) 67%, rgba(255,255,255,0.1) 100%)`,
                pointerEvents: 'none'
              }}
            />
          )}
          {/* Down Arrow Button */}
          <AbsoluteBox bottom="0px" right="-2px" height={52}>
            <SvgIconButton disabled={maxHeight} onClick={handleScrollDownClick} data-testid="scroll-down-button">
              <SvgIcon name="chevron_down_circle" size={52} />
            </SvgIconButton>
          </AbsoluteBox>
        </>
      )}
    </FlexBox>
  );
};
